/**
 * Client API tipizat. Căi relative (`/api/...`, `/api/auth/...`) — first-party prin
 * proxy-ul Vite în dev, `VITE_API_BASE` în producție. Cookie de sesiune inclus.
 */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';

export interface ApiErrorShape {
  code?: string;
  message: string;
  details?: unknown;
  statusCode?: number;
}

export class ApiError extends Error {
  readonly code?: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(status: number, body: ApiErrorShape) {
    super(body.message || `Eroare (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

function buildUrl(path: string, query?: Query): string {
  const url = API_BASE + path;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = { message: text };
    }
  }
  if (!res.ok) {
    const shape = (body ?? {}) as Record<string, unknown>;
    throw new ApiError(res.status, {
      code: typeof shape.code === 'string' ? shape.code : undefined,
      message:
        typeof shape.message === 'string'
          ? shape.message
          : Array.isArray(shape.message)
            ? (shape.message as string[]).join(', ')
            : `Eroare (${res.status})`,
      details: shape.details,
      statusCode: res.status,
    });
  }
  return body as T;
}

async function request<T>(
  method: string,
  path: string,
  opts: { body?: unknown; query?: Query } = {},
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  };
  if (opts.body !== undefined) {
    (init.headers as Record<string, string>)['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }
  const res = await fetch(buildUrl(path, opts.query), init);
  return parse<T>(res);
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown, query?: Query) => request<T>('POST', path, { body, query }),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body }),
  del: <T>(path: string, body?: unknown) => request<T>('DELETE', path, { body }),
};

/** Mesaj de eroare pe înțelesul omului (spec §3), cu fallback generic. */
export function humanError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'REGEN_LIMIT':
        return err.message || 'Ai atins limita de regenerări gratuite pentru acest clip.';
      case 'PRONUNCIATION_LIMIT':
        return 'Ai atins limita de corecții de pronunție gratuite pentru acest clip.';
      case 'INSUFFICIENT_BALANCE':
        return 'Nu ai suficiente clipuri în sold pentru această acțiune.';
      case 'GENERATION_PAUSED':
        return 'Generările sunt momentan oprite. Încearcă din nou în câteva minute.';
      case 'FILE_IN_USE':
        return 'Fișierul este folosit într-un clip și nu poate fi șters.';
      default:
        return err.message;
    }
  }
  if (err instanceof Error) return err.message;
  return 'A apărut o eroare. Încearcă din nou.';
}
